import React, { useEffect } from 'react';
import { Box, IconButton, useTheme, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Button } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'src/store/hooks';

import colors from 'src/styles/equalution/colors';
import CoachingScreenTitleImage from 'src/assets/images/coaching-screen-title.svg';
import SupplementsPackImage from 'src/assets/images/supplements-pack.png';
import CoachingIcon from 'src/assets/icons/coaching.svg';

import { isUserFromAustralia } from 'src/lib/utils';
import {
  setIsNutritionCoachingAdded,
  setIsSupplementAdded,
} from 'src/store/signup';
import {
  trackSubscriptionUpsellViewed,
  trackSubscriptionAddonToggle,
} from 'src/util/track';
import AddonCard from './AddonCard';
import { useAddonFeatures } from '../../hooks/useAddonFeatures';
import { isPlanGiftEligible } from '../gift-with-purchase/eligibility';
import { persistCoachingAddonToSession } from 'src/util/addon-storage.util';

interface AddonUpsellProps {
  coachingPrice: number;
  supplementsPrice: number;
  onContinue: () => void;
  onClose: () => void;
}

const AddonUpsell: React.FC<AddonUpsellProps> = ({
  coachingPrice = 75,
  supplementsPrice = 95,
  onClose,
  onContinue,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();

  const {
    isNutritionCoachingAdded,
    isSupplementAdded,
    availablePlans,
    selectedPlanId,
    shopifyVariants,
    shopifyVariantsLoaded,
  } = useAppSelector(state => state.signUp);

  const computedFeatures = useAddonFeatures();
  const isAustralianUser = isUserFromAustralia();

  const selectedPlan =
    availablePlans.find(p => p.id === selectedPlanId) ?? null;
  const hideSupplementForGift = isPlanGiftEligible(selectedPlan);
  const supplementInStock = shopifyVariants.some(
    v => v.is_protein_flavour === 1
  );
  // Wait until stock check finishes to decide. Rendering before the response
  // would flash the card on/off and let users opt into an out-of-stock pack.
  const showSupplementCard =
    shopifyVariantsLoaded && !hideSupplementForGift && supplementInStock;

  useEffect(() => {
    if (!isAustralianUser) {
      return;
    }

    trackSubscriptionUpsellViewed(
      showSupplementCard
        ? 'nutrition_coaching,supplement_pack'
        : 'nutrition_coaching',
      selectedPlanId || undefined
    );
  }, [isAustralianUser, selectedPlanId, showSupplementCard]);

  if (!isAustralianUser) return null;

  const handleContinue = () => {
    onContinue();
  };

  const handleToggleCoaching = () => {
    const nextState = !isNutritionCoachingAdded;
    trackSubscriptionAddonToggle(
      nextState,
      'nutrition_coaching',
      selectedPlanId || undefined
    );
    persistCoachingAddonToSession(nextState);
    dispatch(setIsNutritionCoachingAdded(nextState));
  };

  const handleToggleSupplements = () => {
    trackSubscriptionAddonToggle(
      !isSupplementAdded,
      'supplement_pack',
      selectedPlanId || undefined
    );
    dispatch(setIsSupplementAdded(!isSupplementAdded));
  };

  return (
    <Box
      maxWidth={isMobile ? 375 : 500}
      mx="auto"
      px={isMobile ? 2 : 5}
      pb={4}
      borderRadius="20px">
      {/* Close Button */}
      <Box textAlign="right" mb={2}>
        <IconButton onClick={onClose} sx={{ p: 0 }}>
          <CloseIcon sx={{ fontSize: 28 }} />
        </IconButton>
      </Box>

      {/* Header Image */}
      <Box mb={6} textAlign="center">
        <img
          src={CoachingScreenTitleImage}
          height={isMobile ? 40 : 55}
          width="auto"
          alt="Elevate your journey"
        />
      </Box>

      {/* Nutrition Coaching Addon Card */}
      <Box mt={3}>
        <AddonCard
          topLabel="You're 95% more likely to hit your goals with a personal nutritionist"
          title="Add 1:1 nutrition coaching"
          priceLabel={`for just $${coachingPrice}/month`}
          icon={CoachingIcon}
          features={[
            'Ongoing accountability and personalised feedback to keep you consistent.',
            "Weekly check-in feedback, tailored education, and expert oversight ensure progress doesn't stall when life gets busy.",
          ]}
          isSelected={Boolean(isNutritionCoachingAdded)}
          onToggle={handleToggleCoaching}
          addLabel="Add nutrition coaching"
          removeLabel="Remove nutrition coaching"
        />
      </Box>

      {/* Supplements Addon Card */}
      {showSupplementCard && (
        <Box>
          <AddonCard
            topLabel="Reach your targets with ease"
            title="Add a Supplement Pack"
            priceLabel={
              <>
                {'for '}
                <Box
                  component="span"
                  sx={{
                    textDecoration: 'line-through',
                    fontWeight: 400,
                    mr: 0.5,
                  }}>
                  ${supplementsPrice + 10}
                </Box>
                ${supplementsPrice}
              </>
            }
            image={SupplementsPackImage}
            features={computedFeatures}
            isSelected={Boolean(isSupplementAdded)}
            onToggle={handleToggleSupplements}
            addLabel="Add Supplement Pack"
            removeLabel="Remove Supplement Pack"
          />
        </Box>
      )}

      {/* Continue Button */}
      <Button
        onClick={handleContinue}
        style={{
          marginBottom: '20px',
          width: '100%',
          fontSize: 16,
          fontWeight: 600,
          padding: 14,
          borderRadius: 30,
          backgroundColor: colors.SALAD,
          color: colors.WHITE,
          border: 'none',
        }}>
        Continue
      </Button>
    </Box>
  );
};

export default AddonUpsell;
